import { useEffect, useRef, useState } from "react"
import { Link } from "react-router"
import { ArrowRight, Plus } from "lucide-react"

import { cn } from "~/lib/utils"
import type { Faq } from "./content"

// Page FAQ. Not added to the FAQPage JSON-LD in root.tsx, which carries the
// site-wide questions.
//
// Collapsed to one line per question (Anna 2026-11-04: "we could collapse this
// little FAQ"), and it plays itself: once on screen, each answer opens for a
// few seconds, closes, and the next one opens (Anna 2026-11-04: "open for a
// fixed time, open and close, one by one"), one at a time. A lime line under the open
// question shows the time left. Any click, hover or focus inside hands it to
// the visitor and the cycle stops for good. Reduced motion: no cycle, all
// closed until clicked. The answers are always in the page, for search.
//
// Also the About page's FAQ (landing/Faq.tsx), with `autoplay` off: there it
// only opens and closes on click (Anna 2026-11-04: "make FAQ more
// interactive with collapsable parts").

const OPEN_MS = 6000
const GAP_MS = 700

export function FaqSection({
  title = "Before switching",
  faqs,
  tone = "paper",
  autoplay = true,
  id,
  defaultOpen = null,
}: {
  title?: string
  faqs: Faq[]
  // The ground, so it alternates with its neighbours on each page.
  tone?: "paper" | "cream"
  // Opens the answers one by one by itself until the visitor takes over.
  autoplay?: boolean
  // The section's anchor (the About FAQ is /about#faq).
  id?: string
  // The answer open when the page loads, if any.
  defaultOpen?: number | null
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState<number | null>(defaultOpen)
  const [auto, setAuto] = useState(autoplay)
  const [seen, setSeen] = useState(false)

  // Start when the list is on screen; pause the clock when it scrolls away.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver(([e]) => setSeen(e.isIntersecting), {
      threshold: 0.5,
    })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  // The list always keeps room for the tallest answer, so opening, closing
  // or switching answers never moves the page below (Anna 2026-11-04: "only
  // one can be open so we don't make other things jump").
  const [reserve, setReserve] = useState<number | undefined>(undefined)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => {
      const rows = [...el.querySelectorAll<HTMLElement>("[data-faq-q]")]
      const answers = [...el.querySelectorAll<HTMLElement>("[data-faq-a]")]
      const closed =
        rows.reduce((h, r) => h + r.offsetHeight, 0) + rows.length + 1
      setReserve(closed + Math.max(0, ...answers.map((a) => a.offsetHeight)))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [faqs])

  useEffect(() => {
    if (
      !auto ||
      !seen ||
      matchMedia("(prefers-reduced-motion: reduce)").matches
    )
      return
    // Only ever one open: the first opens after a beat, then each hands
    // straight over to the next, with no all-closed moment in between.
    const t =
      open === null
        ? setTimeout(() => setOpen(0), GAP_MS)
        : setTimeout(() => setOpen((open + 1) % faqs.length), OPEN_MS)
    return () => clearTimeout(t)
  }, [auto, seen, open, faqs.length])

  // The visitor takes over: stop the cycle for good.
  const takeOver = () => auto && setAuto(false)

  return (
    <section
      id={id}
      className={cn(
        "scroll-mt-24 py-16 sm:py-24",
        tone === "cream" ? "bg-cream" : "bg-paper",
      )}
    >
      <div className="u-container max-w-3xl">
        <div className="text-center">
          <h2 className="font-sans text-[26px] font-semibold leading-[1.15] tracking-tight text-ink sm:text-4xl">
            {title}
          </h2>
        </div>
        <div className="mt-10" style={{ minHeight: reserve }}>
          <div
            ref={ref}
            onPointerEnter={takeOver}
            onFocusCapture={takeOver}
            className="divide-y divide-line border-y border-line"
          >
            {faqs.map(({ q, a, link }, i) => {
              const isOpen = open === i
              const answerId = `${id ?? "faq"}-${i}`
              return (
                <div key={q} className="relative">
                  <h3 data-faq-q>
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={answerId}
                      onClick={() => {
                        takeOver()
                        setOpen(isOpen ? null : i)
                      }}
                      className="flex w-full items-center justify-between gap-6 py-5 text-left font-sans text-lg font-semibold text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest"
                    >
                      {q}
                      <Plus
                        className={cn(
                          "h-5 w-5 shrink-0 text-chartreuse-deep transition-transform duration-300",
                          isOpen && "rotate-45",
                        )}
                        strokeWidth={2}
                        aria-hidden
                      />
                    </button>
                  </h3>
                  {/* Height eases open and shut (grid rows 0fr to 1fr). */}
                  <div
                    id={answerId}
                    role="region"
                    className={cn(
                      "grid transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
                      isOpen
                        ? "grid-rows-[1fr] opacity-100"
                        : "grid-rows-[0fr] opacity-0",
                    )}
                  >
                    <div className="overflow-hidden">
                      <div data-faq-a className="pb-6 pr-11">
                        <p className="text-[17px] leading-relaxed text-mute">
                          {a}
                        </p>
                        {link ? (
                          <Link
                            to={link.href}
                            tabIndex={isOpen ? undefined : -1}
                            className="group mt-3 flex w-fit items-center gap-1 text-[15px] font-medium text-chartreuse-deep hover:text-forest"
                          >
                            {link.label}
                            <ArrowRight
                              className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
                              strokeWidth={2}
                              aria-hidden
                            />
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  {/* The time left on the open answer, while it plays itself */}
                  {/* only while on screen, so it runs with the timer */}
                  {auto && seen && isOpen ? (
                    <span
                      aria-hidden
                      key={`bar-${i}`}
                      style={{ animationDuration: `${OPEN_MS}ms` }}
                      className="absolute -bottom-px left-0 h-px w-full origin-left animate-[faq-bar_linear_forwards] bg-chartreuse-deep"
                    />
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>
        <style>{`@keyframes faq-bar { from { transform: scaleX(0) } to { transform: scaleX(1) } }`}</style>
      </div>
    </section>
  )
}
